import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, watch, onMounted } from 'vue';
import store from "@/store";
import axios from "axios";

interface Props {
  groupedMemberData?: any[];
  lebYm?: string;
  taxData?: any;
  settingsData?: any;
  sabokStaffData?: any[];
  sabokStaffSettings?: any;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MagamBtn',
  props: {
    groupedMemberData: {},
    lebYm: {},
    taxData: {},
    settingsData: {},
    sabokStaffData: {},
    sabokStaffSettings: {}
  },
  emits: ["magamStatusChanged"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// 마감 상태
const isMagam = ref(false);
const loading = ref(false);

// 만나이 계산 함수
const calculateAge = (birthDateStr: string, baseYm: string) => {
  if (!birthDateStr || !baseYm) return 0;

  const birthDate = new Date(birthDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));

  // 기준일 설정 (해당 월의 마지막 날)
  const baseDate = new Date(baseYear, baseMonth, 0);

  // 만 나이 계산
  let age = baseDate.getFullYear() - birthDate.getFullYear();

  // 생일이 지나지 않았으면 1살 빼기
  if (
    baseDate.getMonth() < birthDate.getMonth() ||
    (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

// 퇴직금 적립 가능 여부 확인 함수 (수가시간 60시간 이상만 체크)
const canApplyRetirement = (groupIndex: number) => {
  if (!props.groupedMemberData || !props.groupedMemberData[groupIndex]) return false;

  const group = props.groupedMemberData[groupIndex];

  // 월 수가시간 60시간 이상 체크
  const sugaHours = parseFloat(String(group.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;

  if (sugaHours < 60) {
    return false;
  }

  return true;
};

// 기관 공제 합계 계산 함수
const getCenterDeductionTotal = (group: any, groupIndex: number) => {
  const retirement = props.settingsData?.[groupIndex]?.applyRetirement && canApplyRetirement(group._originalIndex)
    ? Math.round((group.totals.totalPay || 0) / 12)
    : 0;

  return (group.insurance.nationalPension.amount || 0) +
    (group.insurance.healthInsurance.amount || 0) +
    (group.insurance.longTermCare.amount || 0) +
    (group.insurance.employmentInsurance.amount || 0) +
    (group.insurance.unemployment.amount || 0) +
    (group.insurance.industrialAccident.amount || 0) +
    retirement;
};

// 참고자료 계산 함수 (총수가금액 - 세전월급여 - 기관 공제금액)
const getComparisonAmount = (group: any, groupIndex: number) => {
  const totalSugaMoney = Number(group.totals.total_suga_money || 0);
  const totalPay = Number(group.totals.totalPay || 0);
  const centerDeduction = getCenterDeductionTotal(group, groupIndex);

  return totalSugaMoney - totalPay - centerDeduction;
};

const getUnsavedSabokStaff = () => {
  if (!Array.isArray(props.sabokStaffData)) return [];
  return props.sabokStaffData.filter((staff: any) => !staff.empCode);
};

const normalizeNumericString = (value: any) => {
  if (!value && value !== 0) return '';
  return String(value).replace(/[^\d]/g, '');
};

const isTimePayFilledForGroup = (group: any, index: number) => {
  if (!group || !group.patientGroups) return true;
  const originalIndex = group._originalIndex ?? index;
  const settings = props.settingsData?.[originalIndex];

  for (const patientGroup of group.patientGroups) {
    if (!patientGroup.items) continue;
    for (const item of patientGroup.items) {
      if (item.suga_name === '중증수급자 가산') continue;
      const timePayFromSettings = settings?.serviceTimePay?.[item.leb_patient_code]?.[item.suga_name];
      const timePayValue = timePayFromSettings ?? item.time_pay ?? '';
      const numericValue = normalizeNumericString(timePayValue);
      if (!numericValue || numericValue === '0') {
        return false;
      }
    }
  }

  return true;
};

const getGroupsMissingTimePay = () => {
  if (!Array.isArray(props.groupedMemberData)) return [];
  const result: string[] = [];

  props.groupedMemberData.forEach((group: any, index: number) => {
    if (!isTimePayFilledForGroup(group, index)) {
      const caregiverName = group?.caregiverInfo?.caregiver_name || `요양보호사 ${index + 1}`;
      result.push(caregiverName);
    }
  });

  return result;
};

const validateMagamPreconditions = () => {
  const errors: string[] = [];

  if (!Array.isArray(props.sabokStaffData) || props.sabokStaffData.length === 0) {
    errors.push('센터장/사회복지사 급여 데이터가 없습니다. 최소 1건 이상 저장해주세요.');
  }

  const unsavedSabok = getUnsavedSabokStaff();
  if (unsavedSabok.length > 0) {
    const names = unsavedSabok
      .map((staff: any) => staff.name || staff.empCode || '이름없음')
      .join(', ');
    errors.push(`센터장/사회복지사 급여 데이터 중 저장되지 않은 행이 있습니다: ${names}`);
  }

  if (!Array.isArray(props.groupedMemberData) || props.groupedMemberData.length === 0) {
    errors.push('요양보호사 급여 데이터가 없습니다. 최소 1건 이상 저장해주세요.');
  } else {
    const groupsWithMissingTimePay = getGroupsMissingTimePay();
    if (groupsWithMissingTimePay.length > 0) {
      errors.push(`요양보호사 시급이 입력되지 않은 인원이 있습니다: ${groupsWithMissingTimePay.join(', ')}`);
    }
  }

  return errors;
};

// 마감 상태 확인
const checkMagamStatus = async () => {
  const sisulCode = store.state.user?.m_sisul_code;
  const lebYm = props.lebYm;
  
  if (!sisulCode || !lebYm) return;
  
  try {
    const response = await axios.get(`/api/calc/wage-magam/${sisulCode}/${lebYm}`);
    if (response.data.success) {
      isMagam.value = response.data.data.isMagam || false;
      emit('magamStatusChanged', isMagam.value);
    }
  } catch (error) {
    console.error('마감 상태 조회 오류:', error);
  }
};

// 마감 해제
const releaseMagam = async () => {
  const sisulCode = store.state.user?.m_sisul_code;
  const lebYm = props.lebYm;
  
  if (!sisulCode || !lebYm) {
    alert('시설코드와 년월을 확인해주세요.');
    return;
  }
  
  if (!confirm('마감을 해제하시겠습니까?')) {
    return;
  }
  
  loading.value = true;
  
  try {
    const response = await axios.put('/api/calc/wage-magam/release', {
      sisulCode,
      lebYm
    });
    
    if (response.data.success) {
      await checkMagamStatus(); // 마감 상태 다시 확인
    } else {
      alert(response.data.message || '마감 해제에 실패했습니다.');
    }
  } catch (error: any) {
    console.error('마감 해제 오류:', error);
    alert(error.response?.data?.message || '마감 해제 중 오류가 발생했습니다.');
  } finally {
    loading.value = false;
  }
};

// 마감 버튼 클릭 핸들러
const handleMagam = async () => {
  const preconditionErrors = validateMagamPreconditions();
  if (preconditionErrors.length > 0) {
    alert(preconditionErrors.join('\n'));
    return;
  }

  // 마감 완료 상태면 마감 해제
  if (isMagam.value) {
    await releaseMagam();
    return;
  }
  
  // 마감 처리 전 확인
  if (!confirm('마감하시겠습니까?')) {
    return;
  }

  // 마감 처리
  const hasYoboData = props.groupedMemberData && props.groupedMemberData.length > 0;
  const hasSabokData = props.sabokStaffData && props.sabokStaffData.length > 0;

  if (!hasYoboData && !hasSabokData) {
    alert("마감할 데이터가 없습니다.");
    return;
  }

  // 시설코드
  const sisulCode = store.state.user?.m_sisul_code || '';

  // 연월
  const lebYm = props.lebYm || '';

  // 연월 포맷팅
  const year = lebYm.substring(0, 4);
  const month = lebYm.substring(4, 6);
  // 2025년 이하 & 5월에만 근로자의날수당 별도 처리 (2026년부터는 일반 휴일수당에 포함)
  const isLaborDayMonth = month === '05' && parseInt(year) <= 2025;

  // 직종별 데이터 구조
  interface JobTypeData {
    count: number;
    wageSum: number;
    employeeDeduction: number;
    centerDeduction: number;
    nonTaxable: number;
    employeeSavings: number;
    centerSavings: number;
    centerProfit: number;
    totalAge: number;
    ageCount: number;
    sugaSum: number;
    pacientCount: number;
  }

  const jobTypeData: { [key: string]: JobTypeData } = {
    '센터장': {
      count: 0,
      wageSum: 0,
      employeeDeduction: 0,
      centerDeduction: 0,
      nonTaxable: 0,
      employeeSavings: 0,
      centerSavings: 0,
      centerProfit: 0,
      totalAge: 0,
      ageCount: 0,
      sugaSum: 0,
      pacientCount: 0
    },
    '사회복지사': {
      count: 0,
      wageSum: 0,
      employeeDeduction: 0,
      centerDeduction: 0,
      nonTaxable: 0,
      employeeSavings: 0,
      centerSavings: 0,
      centerProfit: 0,
      totalAge: 0,
      ageCount: 0,
      sugaSum: 0,
      pacientCount: 0
    },
    '요양보호사': {
      count: 0,
      wageSum: 0,
      employeeDeduction: 0,
      centerDeduction: 0,
      nonTaxable: 0,
      employeeSavings: 0,
      centerSavings: 0,
      centerProfit: 0,
      totalAge: 0,
      ageCount: 0,
      sugaSum: 0,
      pacientCount: 0
    }
  };

  // 요양보호사 수급자 수 계산을 위한 Set
  const uniquePatients = new Set<string>();

  // 요양보호사 데이터 집계
  if (hasYoboData && props.groupedMemberData) {
    props.groupedMemberData.forEach((group: any, index: number) => {
      const groupIndex = index;
      const originalIndex = group._originalIndex || groupIndex;
      const jobType = '요양보호사';

      // 요양보호사 수
      jobTypeData[jobType].count++;
      
      // 수급자 수 집계 (patientGroups에서 고유한 수급자 코드 추출)
      if (group.patientGroups && Array.isArray(group.patientGroups)) {
        group.patientGroups.forEach((patientGroup: any) => {
          if (patientGroup.patient_code) {
            uniquePatients.add(patientGroup.patient_code);
          }
        });
      }

      // 기본 급여 계산
      const basicPay = (props.taxData?.tr_time_pay || 0) * group.totals.suga_hours;
      const weeklyPay = (props.taxData?.tr_time_pay || 0) * group.totals.suga_hours * 20 / 100;
      const annualPay = Math.round((props.taxData?.tr_time_pay || 0) * group.totals.suga_hours / group.totals.service_days);
      const overtimePay = group.allowances.overtime || 0;
      const nightPay = 0;
      const holidayPay = group.allowances.holiday || 0;
      const laborDayPay = group.allowances.laborDay || 0;
      const longServicePay = group.allowances.longService || 0;
      const severeCareAddition = group.allowances.severeCareAddition || 0;
      const mealPay = group.allowances.meal || 0;
      const otherPay = group.allowances.other || 0;

      // 임금 합계
      const wageSum = basicPay + weeklyPay + annualPay + overtimePay + nightPay + holidayPay + laborDayPay + longServicePay + severeCareAddition + mealPay + otherPay;
      jobTypeData[jobType].wageSum += wageSum;

      // 공제 (근로자부담)
      const nationalPension = group.insurance.nationalPension.amount || 0;
      const healthInsurance = group.insurance.healthInsurance.amount || 0;
      const longTermCare = group.insurance.longTermCare.amount || 0;
      const employmentInsurance = group.insurance.employmentInsurance.amount || 0;
      const incomeTax = group.tax.incomeTax || 0;
      const localTax = group.tax.localTax || 0;
      const supportDeduction = group.insurance.supportDeduction || 0;

      const employeeDeductionSum = nationalPension + healthInsurance + longTermCare + employmentInsurance + incomeTax + localTax + supportDeduction;
      jobTypeData[jobType].employeeDeduction += employeeDeductionSum;

      // 공제 (사업주부담)
      const centerPension = nationalPension;
      const centerHealth = healthInsurance;
      const centerLongTermCare = longTermCare;
      const centerEmployment = employmentInsurance;
      const unemployment = group.insurance.unemployment.amount || 0;
      const industrialAccident = group.insurance.industrialAccident.amount || 0;
      const retirement = props.settingsData?.[originalIndex]?.applyRetirement && canApplyRetirement(originalIndex)
        ? Math.round((group.totals.totalPay || 0) / 12)
        : 0;

      const centerDeductionSum = centerPension + centerHealth + centerLongTermCare + centerEmployment + unemployment + industrialAccident + retirement;
      jobTypeData[jobType].centerDeduction += centerDeductionSum;

      // 비과세액
      const nonTaxable = group.totals.nonTaxableAmount || 0;
      jobTypeData[jobType].nonTaxable += nonTaxable;

      // 실수령증가액 (근로자 절감액)
      const employeeSavings = group.totals.employeeTaxSavings || 0;
      jobTypeData[jobType].employeeSavings += employeeSavings;

      // 인건비절감액 (센터 절감액)
      const centerSavings = group.totals.centerTaxSavings || 0;
      jobTypeData[jobType].centerSavings += centerSavings;

      // 기관수익
      const centerProfit = getComparisonAmount(group, originalIndex);
      jobTypeData[jobType].centerProfit += centerProfit;

      // 총 수가금액
      const sugaMoney = Number(group.totals.total_suga_money || 0);
      jobTypeData[jobType].sugaSum += sugaMoney;

      // 만나이 계산
      const birthDate = group.caregiverInfo.caregiver_birth || '';
      if (birthDate && lebYm) {
        const age = calculateAge(birthDate, lebYm);
        jobTypeData[jobType].totalAge += age;
        jobTypeData[jobType].ageCount++;
      }
    });
    
    // 요양보호사 수급자 수 저장
    jobTypeData['요양보호사'].pacientCount = uniquePatients.size;
  }

  // 사복직원 데이터 집계
  if (hasSabokData && props.sabokStaffData) {
    props.sabokStaffData.forEach((staff: any, index: number) => {
      const settings = props.sabokStaffSettings?.[index];

      // 직종 확인
      const jobType = settings?.type || staff.type || '';
      
      // 직종별 데이터에 없는 직종이면 추가
      if (jobType && !jobTypeData[jobType]) {
        jobTypeData[jobType] = {
          count: 0,
          wageSum: 0,
          employeeDeduction: 0,
          centerDeduction: 0,
          nonTaxable: 0,
          employeeSavings: 0,
          centerSavings: 0,
          centerProfit: 0,
          totalAge: 0,
          ageCount: 0,
          sugaSum: 0,
          pacientCount: 0
        };
      }

      if (jobType) {
        jobTypeData[jobType].count++;
      }

      // settings가 없으면 staffData의 기본 정보를 사용
      if (!settings) {
        const timePay = staff.timePay || 0;
        const workHours = staff.workHours || 0;
        const totalPay = staff.salary || Math.floor(timePay * workHours);

        // 센터장이나 사회복지사인 경우 기본급에서 주휴수당 분리
        let basicPay = totalPay;
        let weeklyPay = 0;
        if (jobType === '센터장' || jobType === '사회복지사') {
          basicPay = Math.floor(totalPay * 5 / 6);
          weeklyPay = totalPay - basicPay;
        }

        const wageSum = basicPay + weeklyPay;
        if (jobType) {
          jobTypeData[jobType].wageSum += wageSum;
        }

        // 만나이 계산
        const birthDate = staff.birth ? (String(staff.birth).includes('-') ? staff.birth : `${staff.birth.substring(0,4)}-${staff.birth.substring(4,6)}-${staff.birth.substring(6,8)}`) : '';
        if (birthDate && lebYm && jobType) {
          const age = calculateAge(birthDate, lebYm);
          jobTypeData[jobType].totalAge += age;
          jobTypeData[jobType].ageCount++;
        }

        return;
      }

      // 기본급 계산
      const timePay = typeof settings.timePay === 'number' ? settings.timePay : parseInt(String(settings.timePay).replace(/,/g, '')) || 0;
      const workHours = typeof settings.workHours === 'number' ? settings.workHours : parseFloat(String(settings.workHours)) || 0;
      const totalPay = Math.floor(timePay * workHours);

      // 센터장이나 사회복지사인 경우 기본급에서 주휴수당 분리
      let basicPay = totalPay;
      let weeklyPay = 0;
      if (jobType === '센터장' || jobType === '사회복지사') {
        basicPay = Math.floor(totalPay * 5 / 6);
        weeklyPay = totalPay - basicPay;
      }

      const annualPay = typeof settings.annualLeaveAllowance === 'number' ? settings.annualLeaveAllowance : parseInt(String(settings.annualLeaveAllowance).replace(/,/g, '')) || 0;
      const overtimePay = typeof settings.overtimeAllowance === 'number' ? settings.overtimeAllowance : parseInt(String(settings.overtimeAllowance).replace(/,/g, '')) || 0;
      const nightPay = typeof settings.nightAllowance === 'number' ? settings.nightAllowance : parseInt(String(settings.nightAllowance).replace(/,/g, '')) || 0;
      const holidayPay = typeof settings.holidayAllowance === 'number' ? settings.holidayAllowance : parseInt(String(settings.holidayAllowance).replace(/,/g, '')) || 0;
      const longServicePay = typeof settings.longServiceAllowance === 'number' ? settings.longServiceAllowance : parseInt(String(settings.longServiceAllowance).replace(/,/g, '')) || 0;
      const mealPay = typeof settings.mealAllowance === 'number' ? settings.mealAllowance : parseInt(String(settings.mealAllowance).replace(/,/g, '')) || 0;
      const transportationFee = typeof settings.transportationFee === 'number' ? settings.transportationFee : parseInt(String(settings.transportationFee).replace(/,/g, '')) || 0;
      const educationFee = typeof settings.educationFee === 'number' ? settings.educationFee : parseInt(String(settings.educationFee).replace(/,/g, '')) || 0;
      const bonus = typeof settings.bonus === 'number' ? settings.bonus : parseInt(String(settings.bonus).replace(/,/g, '')) || 0;
      const vehicleAllowance = typeof settings.vehicleAllowance === 'number' ? settings.vehicleAllowance : parseInt(String(settings.vehicleAllowance).replace(/,/g, '')) || 0;
      const childEducationFee = typeof settings.childEducationFee === 'number' ? settings.childEducationFee : parseInt(String(settings.childEducationFee).replace(/,/g, '')) || 0;
      const otherPay = typeof settings.otherAllowance === 'number' ? settings.otherAllowance : parseInt(String(settings.otherAllowance).replace(/,/g, '')) || 0;

      // 임금 합계
      const wageSum = basicPay + weeklyPay + annualPay + overtimePay + nightPay + holidayPay + longServicePay + mealPay + transportationFee + educationFee + bonus + vehicleAllowance + childEducationFee + otherPay;
      if (jobType) {
        jobTypeData[jobType].wageSum += wageSum;
      }

      // 공제 (근로자부담)
      const nationalPension = settings.insurance?.nationalPension?.amount || 0;
      const healthInsurance = settings.insurance?.healthInsurance?.amount || 0;
      const longTermCare = settings.insurance?.longTermCare?.amount || 0;
      const employmentInsurance = settings.insurance?.employmentInsurance?.amount || 0;
      const incomeTax = settings.tax?.incomeTax || 0;
      const localTax = settings.tax?.localTax || 0;
      const supportDeduction = settings.insurance?.supportDeduction?.amount || 0;

      const employeeDeductionSum = nationalPension + healthInsurance + longTermCare + employmentInsurance + incomeTax + localTax + supportDeduction;
      if (jobType) {
        jobTypeData[jobType].employeeDeduction += employeeDeductionSum;
      }

      // 공제 (사업주부담)
      const centerPension = nationalPension;
      const centerHealth = healthInsurance;
      const centerLongTermCare = longTermCare;
      const centerEmployment = employmentInsurance;
      const unemployment = settings.insurance?.unemployment?.amount || 0;
      const industrialAccident = settings.insurance?.industrialAccident?.amount || 0;
      const retirement = settings.insurance?.retirement?.amount || 0;

      const centerDeductionSum = centerPension + centerHealth + centerLongTermCare + centerEmployment + unemployment + industrialAccident + retirement;
      if (jobType) {
        jobTypeData[jobType].centerDeduction += centerDeductionSum;
      }

      // 비과세액
      const nonTaxable = settings.totals?.nonTaxableAmount || 0;
      if (jobType) {
        jobTypeData[jobType].nonTaxable += nonTaxable;
      }

      // 실수령증가액 (근로자 절감액)
      const employeeSavings = settings.totals?.employeeTaxSavings || 0;
      if (jobType) {
        jobTypeData[jobType].employeeSavings += employeeSavings;
      }

      // 인건비절감액 (센터 절감액)
      const centerSavings = settings.totals?.centerTaxSavings || 0;
      if (jobType) {
        jobTypeData[jobType].centerSavings += centerSavings;
      }

      // 만나이 계산
      const birthDate = settings.birthDate || (staff.birth ? (String(staff.birth).includes('-') ? staff.birth : `${staff.birth.substring(0,4)}-${staff.birth.substring(4,6)}-${staff.birth.substring(6,8)}`) : '');
      if (birthDate && lebYm && jobType) {
        const age = calculateAge(birthDate, lebYm);
        jobTypeData[jobType].totalAge += age;
        jobTypeData[jobType].ageCount++;
      }
    });
  }

  // 전체 합계 계산
  let totalCount = 0;
  let totalWageSum = 0;
  let totalEmployeeDeduction = 0;
  let totalCenterDeduction = 0;
  let totalNonTaxable = 0;
  let totalEmployeeSavings = 0;
  let totalCenterSavings = 0;
  let totalCenterProfit = 0;
  let totalAge = 0;
  let totalAgeCount = 0;
  let totalSugaSum = 0;

  Object.values(jobTypeData).forEach(data => {
    totalCount += data.count;
    totalWageSum += data.wageSum;
    totalEmployeeDeduction += data.employeeDeduction;
    totalCenterDeduction += data.centerDeduction;
    totalNonTaxable += data.nonTaxable;
    totalEmployeeSavings += data.employeeSavings;
    totalCenterSavings += data.centerSavings;
    totalCenterProfit += data.centerProfit;
    totalAge += data.totalAge;
    totalAgeCount += data.ageCount;
    totalSugaSum += data.sugaSum;
  });

  const averageAge = totalAgeCount > 0 ? Math.round(totalAge / totalAgeCount) : 0;

  // DB 저장 데이터 준비
  const magamData = [];

  // 직종별 데이터 저장
  Object.keys(jobTypeData).forEach(jobType => {
    const data = jobTypeData[jobType];
    if (data.count > 0) {
      const avgAge = data.ageCount > 0 ? Math.round(data.totalAge / data.ageCount) : 0;
      magamData.push({
        wm_sisul_code: sisulCode,
        wm_ym: lebYm,
        wm_job_type: jobType,
        wm_count: data.count,
        wm_wage_sum: data.wageSum,
        wm_emp_deduction: data.employeeDeduction,
        wm_center_deduction: data.centerDeduction,
        wm_non_taxable: data.nonTaxable,
        wm_emp_savings: data.employeeSavings,
        wm_center_savings: data.centerSavings,
        wm_center_profit: data.centerProfit,
        wm_avg_age: avgAge,
        wm_suga_sum: data.sugaSum,
        wm_pacient_count: jobType === '요양보호사' ? data.pacientCount : 0
      });
    }
  });

  // 전체 합계 데이터 저장
  magamData.push({
    wm_sisul_code: sisulCode,
    wm_ym: lebYm,
    wm_job_type: '전체합계',
    wm_count: totalCount,
    wm_wage_sum: totalWageSum,
    wm_emp_deduction: totalEmployeeDeduction,
    wm_center_deduction: totalCenterDeduction,
    wm_non_taxable: totalNonTaxable,
    wm_emp_savings: totalEmployeeSavings,
    wm_center_savings: totalCenterSavings,
    wm_center_profit: totalCenterProfit,
    wm_avg_age: averageAge,
    wm_suga_sum: totalSugaSum,
    wm_pacient_count: jobTypeData['요양보호사'].pacientCount
  });

  // DB 저장 API 호출
  loading.value = true;
  
  try {
    const response = await axios.post('/api/calc/wage-magam', {
      sisulCode: sisulCode,
      lebYm: lebYm,
      magamData: magamData
    });

    if (response.data.success) {
      // 마감 상태 다시 확인
      await checkMagamStatus();
    } else {
      console.error('마감 데이터 저장 실패:', response.data.message);
      alert('마감 데이터 저장에 실패했습니다: ' + (response.data.message || '알 수 없는 오류'));
    }
  } catch (error: any) {
    console.error('마감 데이터 저장 오류:', error);
    alert('마감 데이터 저장 중 오류가 발생했습니다: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value = false;
  }
};

// 페이지 로드 시 마감 상태 확인
onMounted(() => {
  checkMagamStatus();
});

// lebYm 변경 시 마감 상태 재확인
watch(() => props.lebYm, () => {
  checkMagamStatus();
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createBlock(_component_v_btn, {
    color: isMagam.value ? 'grey-darken-1' : 'success',
    variant: "elevated",
    class: "magam-btn",
    onClick: handleMagam,
    loading: loading.value
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_icon, {
        size: "18",
        class: "mr-1"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(isMagam.value ? 'mdi-lock' : 'mdi-lock-open-variant'), 1)
        ]),
        _: 1
      }),
      _createElementVNode("span", null, _toDisplayString(isMagam.value ? '마감완료' : '마감'), 1)
    ]),
    _: 1
  }, 8, ["color", "loading"]))
}
}

})